import { useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import { commitAlumniImport, parseAlumniImport, previewAlumniImport } from '../../api/alumni';
import { AlumniLookups, ImportParseResult, ImportPreview, ImportResult } from '../../types/alumni';
import { DUPLICATE_REASON_LABELS } from './alumniLabels';

const errorText = (err: unknown, fallback: string) =>
  isAxiosError(err) ? (err.response?.data?.message ?? fallback) : fallback;

const STEPS = ['Upload file', 'Map columns', 'Preview', 'Done'];
const OUTCOME_CHIP = {
  ready: { label: 'Will import', color: 'success' as const },
  duplicate: { label: 'Possible duplicate', color: 'warning' as const },
  invalid: { label: 'Has errors', color: 'error' as const },
};

/** Import alumni from Excel or CSV (Phase 105): upload → map each column
 * to an alumni field (suggested from the headings) → preview every row with
 * its errors and possible duplicates → import. Only new records are
 * created; rows that look like duplicates are skipped unless ticked, and
 * rows with errors are never imported. */
export const AlumniImportDialog = ({
  open,
  onClose,
  onImported,
  lookups,
}: {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
  lookups: AlumniLookups | undefined;
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const inputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState(0);
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState<ImportParseResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [branchId, setBranchId] = useState<number | ''>('');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [include, setInclude] = useState<Set<number>>(new Set());
  const [result, setResult] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setStep(0);
    setFileName('');
    setParsed(null);
    setMapping({});
    setBranchId('');
    setPreview(null);
    setInclude(new Set());
    setResult(null);
    setError(null);
  };
  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const payload = () => ({
    headers: parsed!.headers,
    rows: parsed!.rows,
    mapping: Object.fromEntries(Object.entries(mapping).filter(([, v]) => v)),
    ...(branchId ? { branchId } : {}),
  });

  const run = async (fn: () => Promise<void>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(errorText(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  const upload = (file: File | undefined) => {
    if (!file) return;
    setFileName(file.name);
    void run(async () => {
      const data = await parseAlumniImport(file);
      setParsed(data);
      setMapping(data.suggestedMapping);
      setStep(1);
    }, "Couldn't read the file");
  };

  const mappedFields = Object.values(mapping).filter(Boolean);
  const missingRequired = (parsed?.fields ?? []).filter((f) => f.required && !mappedFields.includes(f.key));
  const doubleMapped = mappedFields.filter((k, i) => mappedFields.indexOf(k) !== i);

  const runPreview = () =>
    run(async () => {
      const data = await previewAlumniImport(payload());
      setPreview(data);
      setInclude(new Set());
      setStep(2);
    }, "Couldn't check the rows");

  const commit = () =>
    run(async () => {
      const data = await commitAlumniImport({ ...payload(), includeDuplicateRows: [...include] });
      setResult(data);
      setStep(3);
      onImported();
    }, "Couldn't import the rows");

  const toImport = preview ? preview.summary.ready + include.size : 0;
  const fieldLabel = (key: string) => parsed?.fields.find((f) => f.key === key)?.label ?? key;

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="lg" fullScreen={fullScreen} aria-labelledby="alumni-import-title">
      <DialogTitle id="alumni-import-title">Import alumni</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((s) => (
            <Step key={s}>
              <StepLabel>{s}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {step === 0 && (
          <Stack spacing={2} alignItems="center" sx={{ py: 3, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ px: 2 }}>
              Upload an Excel (.xlsx) or CSV file whose first row holds the column headings — up to 2,000 rows. Full name
              and contact number are required for every row. Nothing is saved until you confirm the preview.
            </Typography>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.csv"
              hidden
              data-testid="alumni-import-file"
              onChange={(e) => {
                upload(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <Button variant="contained" startIcon={<UploadFileIcon />} disabled={busy} onClick={() => inputRef.current?.click()}>
              {busy ? 'Reading…' : 'Choose file'}
            </Button>
          </Stack>
        )}

        {step === 1 && parsed && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {fileName}: {parsed.rows.length} row{parsed.rows.length === 1 ? '' : 's'}. Choose which alumni field each
              column fills, or "Don't import" to skip it.
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 560 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Column in your file</TableCell>
                    <TableCell>Example</TableCell>
                    <TableCell sx={{ width: 260 }}>Alumni field</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsed.headers.map((h, i) => (
                    <TableRow key={i}>
                      <TableCell>{h}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {parsed.rows.find((r) => r[i])?.[i] ?? ''}
                      </TableCell>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={mapping[String(i)] ?? ''}
                          onChange={(e) => setMapping((m) => ({ ...m, [String(i)]: e.target.value }))}
                          inputProps={{ 'aria-label': `Field for column ${h}` }}
                          error={!!mapping[String(i)] && doubleMapped.includes(mapping[String(i)])}
                        >
                          <MenuItem value="">Don't import</MenuItem>
                          {parsed.fields.map((f) => (
                            <MenuItem key={f.key} value={f.key}>
                              {f.label}
                              {f.required ? ' *' : ''}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {missingRequired.length > 0 && (
              <Alert severity="warning">Map a column to: {missingRequired.map((f) => f.label).join(', ')}.</Alert>
            )}
            {doubleMapped.length > 0 && (
              <Alert severity="warning">Each field can be used for one column only: {[...new Set(doubleMapped)].map(fieldLabel).join(', ')}.</Alert>
            )}
            {lookups?.canChooseBranch && (
              <TextField select size="small" label="Branch for these records" value={branchId} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={{ maxWidth: 320 }} onChange={(e) => setBranchId(e.target.value === '' ? '' : Number(e.target.value))}>
                <MenuItem value="">Default (linked student's or your own)</MenuItem>
                {lookups.branches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>
        )}

        {step === 2 && preview && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip color="success" label={`${preview.summary.ready} ready`} />
              <Chip color="warning" label={`${preview.summary.duplicates} possible duplicates`} />
              <Chip color="error" label={`${preview.summary.invalid} with errors`} />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Rows with errors won't be imported. Possible duplicates are skipped unless you tick them — existing records
              are never changed.
            </Typography>
            <Box sx={{ overflowX: 'auto', maxHeight: 420 }}>
              <Table size="small" stickyHeader sx={{ minWidth: 760 }} data-testid="alumni-import-preview">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">Import</TableCell>
                    <TableCell>Row</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Contact / email</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.rows.map((r) => (
                    <TableRow key={r.rowNumber}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={r.outcome === 'ready' || include.has(r.rowNumber)}
                          disabled={r.outcome !== 'duplicate'}
                          inputProps={{ 'aria-label': `Import row ${r.rowNumber}` }}
                          onChange={(e) =>
                            setInclude((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(r.rowNumber);
                              else next.delete(r.rowNumber);
                              return next;
                            })
                          }
                        />
                      </TableCell>
                      <TableCell>{r.rowNumber}</TableCell>
                      <TableCell>{r.values.fullName ?? '—'}</TableCell>
                      <TableCell>{[r.values.phone, r.values.email].filter(Boolean).join(' · ')}</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" color={OUTCOME_CHIP[r.outcome].color} label={OUTCOME_CHIP[r.outcome].label} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        {r.errors.map((e) => (
                          <Typography key={e} variant="caption" color="error" display="block">
                            {e}
                          </Typography>
                        ))}
                        {r.duplicates.map((d, i) => (
                          <Typography key={i} variant="caption" color="warning.dark" display="block">
                            {d.row ? `Same as row ${d.row}` : `Matches ${d.alumniNumber}${d.fullName ? ` (${d.fullName})` : ''}`} —{' '}
                            {d.reasons.map((x) => DUPLICATE_REASON_LABELS[x] ?? x).join(', ')}
                          </Typography>
                        ))}
                        {r.linkStudent && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Will link to student {r.linkStudent.studentNumber}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        )}

        {step === 3 && result && (
          <Stack spacing={2}>
            <Alert severity={result.summary.created > 0 ? 'success' : 'info'}>
              Imported {result.summary.created} of {result.summary.total} row{result.summary.total === 1 ? '' : 's'}.
              {result.summary.skipped > 0 ? ` ${result.summary.skipped} skipped.` : ''}
            </Alert>
            {result.skipped.length > 0 && (
              <Box sx={{ maxHeight: 260, overflow: 'auto' }}>
                {result.skipped.map((s) => (
                  <Typography key={s.rowNumber} variant="body2">
                    Row {s.rowNumber}: {s.reason}
                  </Typography>
                ))}
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        {step < 3 && (
          <Button onClick={close} disabled={busy}>
            Cancel
          </Button>
        )}
        {(step === 1 || step === 2) && (
          <Button onClick={() => setStep(step - 1)} disabled={busy}>
            Back
          </Button>
        )}
        {step === 1 && (
          <Button variant="contained" disabled={busy || missingRequired.length > 0 || doubleMapped.length > 0} onClick={() => void runPreview()}>
            {busy ? 'Checking…' : 'Preview'}
          </Button>
        )}
        {step === 2 && (
          <Button variant="contained" disabled={busy || toImport === 0} onClick={() => void commit()}>
            {busy ? 'Importing…' : `Import ${toImport} record${toImport === 1 ? '' : 's'}`}
          </Button>
        )}
        {step === 3 && (
          <Button variant="contained" onClick={close}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
